import { Button, Tooltip } from '@ads/components-react';
import { InformationCircle } from '@ads/icons';
import { brandLinka } from '@ads/tokens';
import { DashboardCheckIn } from '@linka/domain/dtos';
import { useTranslation } from '@linka/i18n';
import { formatDate } from '@linka/utils';
import { useNavigate } from 'react-router';
import { Bar, BarChart, CartesianGrid, LabelList, XAxis, YAxis } from 'recharts';
import { ChartContainer, ChartTooltip, ChartTooltipContent } from '../../../chart/chart';
import {
  ChartCard,
  ChartCardContent,
  ChartCardFooter,
  ChartCardHeader,
  ChartCardTitle,
  ExportChartTriggerButton,
} from '../components';
import { useSearchFilters } from '../hooks/use-search-filters';
import { firstSegmentLabel, firstSegmentShape, hideZeroLabel } from './stacked-bar';

interface CheckInChartProps {
  data: DashboardCheckIn;
}
export const CheckInChart = ({ data }: CheckInChartProps) => {
  const navigate = useNavigate();
  const t = useTranslation();
  const { deleteFilter } = useSearchFilters();

  const chartConfig = {
    checkedInCount: { label: t('checked-in-at-least-once'), color: brandLinka.baseColor.brand['400'] },
    notCheckedInCount: { label: t('not-checked-in'), color: brandLinka.contextualColor.interactive.primary },
  };

  return (
    <ChartCard>
      <ChartCardHeader>
        <div className='flex items-center gap-nano'>
          <ChartCardTitle>{t('dashboard.charts.biped-participants')}</ChartCardTitle>
          <Tooltip title={t('dashboard.checkin-disclaimer')}>
            <InformationCircle className='text-ctx-content-base mt-px' size={16} />
          </Tooltip>
        </div>
        <ExportChartTriggerButton type='OVERVIEW_CHECKIN' />
      </ChartCardHeader>
      {data.data.length === 0 ? (
        <ChartCardContent className='flex flex-1 items-center justify-center py-xl'>
          <span className='ts-paragraph-xs text-ctx-content-base'>{t('dashboard.charts.no-data')}</span>
        </ChartCardContent>
      ) : (
        <ChartCardContent className='flex flex-col'>
          <div className='flex items-center justify-end gap-sm pb-nano'>
            {Object.entries(chartConfig).map(([key, config]) => (
              <div key={key} className='flex items-center gap-quark'>
                <div className='h-3 w-3 shrink-0 rounded-sm' style={{ background: config.color }} />
                <span className='ts-caption-xxs text-ctx-content-base'>{config.label}</span>
              </div>
            ))}
          </div>

          <div className='flex flex-1'>
            <ChartContainer config={chartConfig} className='w-[60px]'>
              <BarChart responsive accessibilityLayer data={data.data} margin={{ top: 24 }} width={60}>
                <CartesianGrid vertical={false} />
                <Bar dataKey='checkedInCount' stackId='a' fill='transparent' barSize={0} />
                <Bar dataKey='notCheckedInCount' stackId='a' fill='transparent' barSize={0} />
                <YAxis tickLine={false} tickMargin={10} axisLine={false} width={60} />
              </BarChart>
            </ChartContainer>

            <div className='flex-1 overflow-x-auto pb-xs'>
              <ChartContainer config={chartConfig} className='w-full' dataLength={data.data.length}>
                <BarChart responsive accessibilityLayer data={data.data} margin={{ top: 24 }}>
                  <CartesianGrid vertical={false} />
                  <Bar
                    dataKey='checkedInCount'
                    stackId='a'
                    fill='var(--color-checkedInCount)'
                    shape={firstSegmentShape('notCheckedInCount')}
                  >
                    <LabelList dataKey='checkedInCount' content={firstSegmentLabel(data.data, 'notCheckedInCount')} />
                  </Bar>
                  <Bar
                    dataKey='notCheckedInCount'
                    stackId='a'
                    fill='var(--color-notCheckedInCount)'
                    radius={[8, 8, 0, 0]}
                  >
                    <LabelList dataKey='notCheckedInCount' position='top' formatter={hideZeroLabel} />
                  </Bar>
                  <XAxis
                    dataKey='date'
                    tickLine={false}
                    tickMargin={10}
                    axisLine={false}
                    tickFormatter={(val: string) => formatDate({ date: val, dateFormat: 'dd/MM/yyyy' }) ?? val}
                  />
                  <ChartTooltip
                    labelFormatter={(label) => formatDate({ date: label, dateFormat: 'dd/MM/yyyy HH:mm' }) ?? label}
                    cursor={{ fill: brandLinka.contextualColor.interactive.secondary }}
                    content={<ChartTooltipContent />}
                  />
                </BarChart>
              </ChartContainer>
            </div>
          </div>
        </ChartCardContent>
      )}
      {data.data.length > 0 && (
        <ChartCardFooter>
          <div className='flex flex-wrap justify-end gap-nano py-nano *:!w-full sm:*:!w-auto'>
            <Button
              className='whitespace-nowrap !text-xxxs sm:!text-xxs'
              onClick={() => {
                deleteFilter('type');
                navigate(`detailed-checkins?type=general`);
              }}
            >
              {t('dashboard.detailed.checkins-performed')}
            </Button>
            <Button
              className='whitespace-nowrap !text-xxxs sm:!text-xxs'
              onClick={() => {
                deleteFilter('type');
                navigate(`detailed-checkins?type=activity`);
              }}
            >
              {t('dashboard.detailed.activity-checkins')}
            </Button>
          </div>
        </ChartCardFooter>
      )}
    </ChartCard>
  );
};
